import { useDebounce, useResponsive } from 'hooks'
import { Post, User } from 'lib/_types'
import { isNil, uniqueId } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import {
  useCurrentUser,
  useSearchMentionableUsersQuery,
} from 'store/api/yay/user'

const isValidMentionableRegex = /^@[^\s]*$/ // Check only no spaces
const isSpace = (t: string, i: number) => [32, 160].includes(t.charCodeAt(i))

function findWordGivenIndex(text: string, index: number) {
  let word = text[index]
  let startOffset = index
  let endOffset = index + 1

  if (word === ' ' || word === undefined) return null

  for (let i = index - 1; i >= 0 && !isSpace(text, i); i--, startOffset--) {
    word = `${text[i]}${word}`
  }

  for (
    let i = index + 1;
    i < text.length && !isSpace(text, i);
    i++, endOffset++
  ) {
    word = `${word}${text[i]}`
  }

  return {
    word,
    startOffset,
    endOffset,
  }
}

export function getMessageTags(html: HTMLElement) {
  const mentionsList: Post['message_tags'] = []
  const mentions = document.querySelectorAll('.Mention')

  let prevOffset = 0

  mentions.forEach((e) => {
    const user_id = e.getAttribute('data-id')
    const length = e.innerHTML.length
    const offset = html.innerText.indexOf(e.textContent, prevOffset)
    prevOffset = offset + 1

    mentionsList.push({
      type: e.getAttribute('data-type') as 'user' | 'all',
      ...(isNil(user_id) ? {} : { user_id: Number(user_id) }),
      offset,
      length,
    })
  })

  return mentionsList
}

const useSearchMentionableList = ({ search }: { search: string }) => {
  const userId = useCurrentUser()?.data?.id
  const { groupId } = useParams()

  const res = useSearchMentionableUsersQuery(
    { userId, search, groupId },
    {
      skip: isNil(search),
    }
  )

  return {
    list: !isNil(search)
      ? res?.data?.list?.filter?.((x) => x?.id != userId) ?? []
      : null,
    isLoading: res.isLoading || res.isUninitialized,
  }
}

export const useMention = ({
  isContentEditableActive,
  postRef,
  updateText,
  isMentionEnabled,
}: {
  isContentEditableActive: boolean
  postRef: React.MutableRefObject<HTMLDivElement>
  updateText: React.Dispatch<React.SetStateAction<string>>
  isMentionEnabled: boolean
}) => {
  const [__mentionSearch, __mentionSearchSet] = useState<string>(null)
  const [__mentionableListStyles, __mentionableListStylesSet] =
    useState<React.CSSProperties>(null)
  const __mentionableRef = useRef<Range>(null)
  const debouncedSearchText = useDebounce(__mentionSearch, 200)
  const { isMobile } = useResponsive()

  const { list, isLoading } = useSearchMentionableList({
    search: debouncedSearchText,
  })

  useEffect(() => {
    let timeout: NodeJS.Timeout

    function resizeListener() {
      timeout = setTimeout(() => refreshMentionListPosition(), 100)
    }

    window.addEventListener('resize', resizeListener)

    return () => {
      window.clearTimeout(timeout)
      window.removeEventListener('resize', resizeListener)
    }
  }, [])

  useEffect(() => {
    if (postRef.current && isMentionEnabled) {
      postRef.current.addEventListener('keydown', addhandler)
      postRef.current.addEventListener('mousedown', addhandler)
      postRef.current.addEventListener('input', removeHandler)
    }

    return () => {
      if (postRef.current) {
        postRef.current?.removeEventListener('keydown', addhandler)
        postRef.current?.removeEventListener('mousedown', addhandler)
        postRef.current?.removeEventListener('input', removeHandler)
      }
    }
  }, [isContentEditableActive, isMentionEnabled])

  const reset = () => {
    __mentionSearchSet(null)
    __mentionableListStylesSet(null)
  }

  const generateMentionSpanTag = ({
    user,
    type = 'user',
  }: {
    user?: User
    type?: 'user' | 'all'
  }) => {
    const span = document.createElement('span')
    span.classList.add('Mention')

    if (type == 'user') {
      span.setAttribute('data-id', user?.id?.toString?.())
    }

    span.setAttribute('data-key', uniqueId())
    span.setAttribute('data-type', type)

    span.textContent = `@${
      type == 'user'
        ? user?.nickname?.replace(
            // all non line-break unicode in list of https://en.wikipedia.org/wiki/Whitespace_character#Unicode
            /\s\u0009\u0020\u00a0\u1680\u180e\u2000-\u200d\u202f\u205f\u2060\u3000\ufeff/g,
            ' '
          )
        : 'all'
    }`

    return span
  }

  const select = ({
    user,
    type = 'user',
  }: {
    user?: User
    type?: 'user' | 'all'
  }) => {
    const span = generateMentionSpanTag({
      user,
      type,
    })

    const space = document.createTextNode('\u00A0')

    __mentionableRef.current.deleteContents()
    __mentionableRef.current.insertNode(span)
    span.after(space)

    const __range = document.createRange()
    __range.setStartAfter(space)
    __range.collapse(true)
    window.getSelection().removeAllRanges()
    window.getSelection().addRange(__range)

    reset()

    updateText(postRef.current.innerText)
  }

  const refreshMentionListPosition = () => {
    if (__mentionableRef.current) {
      const mentionableRect = __mentionableRef.current?.getBoundingClientRect()
      const mentionPosition: React.CSSProperties = {}

      if (isMobile) {
        mentionPosition.left = postRef.current?.getBoundingClientRect().left
        mentionPosition.right = postRef.current?.getBoundingClientRect().left
      } else {
        mentionPosition.left = mentionableRect.left
      }

      if (mentionableRect.top > window.innerHeight / 2) {
        mentionPosition.bottom = window.innerHeight - mentionableRect.top + 20
      } else {
        mentionPosition.top = mentionableRect.bottom + 20
      }

      __mentionableListStylesSet(mentionPosition)
    }
  }

  const addhandler = useCallback(() => {
    setTimeout(() => {
      const selection = window.getSelection()
      const offset = selection.focusOffset - 1

      const {
        word: focusedWord,
        startOffset,
        endOffset,
      } = findWordGivenIndex(selection.focusNode.textContent, offset) ?? {}

      const isValidMention =
        selection.focusNode.nodeName === '#text' &&
        !selection.focusNode.parentElement.classList.contains('Mention') &&
        isValidMentionableRegex.test(focusedWord)

      if (isValidMention) {
        const range = document.createRange()
        range.setStart(selection.focusNode, startOffset)
        range.setEnd(selection.focusNode, endOffset)
        __mentionableRef.current = range
        __mentionSearchSet(focusedWord.slice(1))
        refreshMentionListPosition()
      } else {
        reset()
      }
    }, 10)
  }, [])

  const removeHandler = useCallback(() => {
    const selection = window.getSelection()

    if (selection.focusNode.parentElement.classList.contains('Mention')) {
      selection.focusNode.parentElement.remove()
      reset()
    }
  }, [])

  return {
    styles: __mentionableListStyles,
    list,
    reset,
    generateMentionSpanTag,
    select,
    isLoading,
  }
}
